import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  useBulkSavePriceOverride,
  useClearLineOverride,
  useLineOverrideHistory,
  useManualMtoLineHistory,
  useSameItemMtos,
  useSaveLineOverride,
  type LineOverrideKind,
} from '../../api/lines';
import { formatSupplierDate, todayIsoDate } from '../../api/suppliers';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { DataTable } from '../ui/DataTable';
import { useToast } from '../../hooks/useToast';
import page from '../../pages/Page.module.css';

function numsEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9;
}

function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

export function lineOverrideKindLabel(
  kind: LineOverrideKind,
  source: 'mto' | 'manual' = 'mto'
): string {
  if (source === 'manual') {
    if (kind === 'qty') return 'Quantity';
    if (kind === 'price') return 'Unit Cost';
    return 'Line Unit Price';
  }
  if (kind === 'qty') return 'Override Quantity';
  if (kind === 'price') return 'Forced Unit Price';
  return 'Line Unit Price';
}

export function lineOverrideEditTitle(kind: LineOverrideKind, itemLabel: string): string {
  return `Edit ${lineOverrideKindLabel(kind)} — ${itemLabel}`;
}

export function lineOverrideHistoryTitle(
  kind: LineOverrideKind,
  itemLabel: string,
  source: 'mto' | 'manual' = 'mto'
): string {
  return `${lineOverrideKindLabel(kind, source)} History — ${itemLabel}`;
}

type HistoryRow = {
  id: number;
  value: number;
  effective_date: string;
  updated_at?: string;
};

export function LineOverrideActionButtons({
  bidLocked,
  hasValue,
  onEdit,
  onHistory,
  onClear,
  isClearPending,
}: {
  bidLocked?: boolean;
  hasValue: boolean;
  onEdit: () => void;
  onHistory: () => void;
  onClear: () => void;
  isClearPending?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {!bidLocked ? (
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
      ) : null}
      <Button type="button" variant="ghost" size="sm" onClick={onHistory}>
        History
      </Button>
      {!bidLocked && hasValue ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isClearPending}
          onClick={onClear}
        >
          Clear
        </Button>
      ) : null}
    </div>
  );
}

export function LineQtyPriceOverrideActions({
  bidLocked,
  hasQtyOverride,
  hasPriceOverride,
  onEdit,
  onQtyHistory,
  onPriceHistory,
  onClearQty,
  onClearPrice,
  isClearPending,
}: {
  bidLocked?: boolean;
  hasQtyOverride: boolean;
  hasPriceOverride: boolean;
  onEdit: () => void;
  onQtyHistory: () => void;
  onPriceHistory: () => void;
  onClearQty: () => void;
  onClearPrice: () => void;
  isClearPending?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {!bidLocked ? (
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
      ) : null}
      <Button type="button" variant="ghost" size="sm" onClick={onQtyHistory}>
        Qty History
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onPriceHistory}>
        Price History
      </Button>
      {!bidLocked && hasQtyOverride ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isClearPending}
          onClick={onClearQty}
        >
          Clear Qty
        </Button>
      ) : null}
      {!bidLocked && hasPriceOverride ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isClearPending}
          onClick={onClearPrice}
        >
          Clear Price
        </Button>
      ) : null}
    </div>
  );
}

export function LineQtyPriceEditModal({
  open,
  lineId,
  itemLabel,
  mtoQty,
  currentQtyOverride,
  currentPriceOverride,
  mtoId,
  communityId,
  bidLocked,
  onClose,
  onSaved,
}: {
  open: boolean;
  lineId: number;
  itemLabel: string;
  mtoQty?: number | null;
  currentQtyOverride?: number | null;
  currentPriceOverride?: number | null;
  mtoId: string | undefined;
  communityId: string | undefined;
  bidLocked?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const saveQty = useSaveLineOverride(mtoId, communityId, 'qty');
  const savePrice = useSaveLineOverride(mtoId, communityId, 'price');
  const saveBulkPrice = useBulkSavePriceOverride(mtoId, communityId);
  const [qty, setQty] = useState(
    currentQtyOverride != null ? String(currentQtyOverride) : ''
  );
  const [price, setPrice] = useState(
    currentPriceOverride != null ? String(currentPriceOverride) : ''
  );
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());
  const [selectedMtoIds, setSelectedMtoIds] = useState<Set<number>>(() => new Set());

  const siblingsQuery = useSameItemMtos(open ? lineId : null, open && !!communityId && !bidLocked);
  const siblingMtos = useMemo(() => siblingsQuery.data?.mtos ?? [], [siblingsQuery.data?.mtos]);

  useEffect(() => {
    if (!open) return;
    setSelectedMtoIds(new Set());
  }, [open, siblingMtos]);

  const showApplyToOthers = price.trim() !== '' && siblingMtos.length > 0;
  const allSelected =
    siblingMtos.length > 0 && siblingMtos.every((m) => selectedMtoIds.has(m.mto_file_id));
  const someSelected = siblingMtos.some((m) => selectedMtoIds.has(m.mto_file_id));
  const selectedSiblingCount = siblingMtos.filter((m) => selectedMtoIds.has(m.mto_file_id)).length;
  const isPending = saveQty.isPending || savePrice.isPending || saveBulkPrice.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit Line Overrides — ${itemLabel}`}
      showClose={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (bidLocked) return;

          const qtyNum = parseOptionalNumber(qty);
          const priceNum = parseOptionalNumber(price);

          if (qty.trim() !== '' && qtyNum == null) {
            toast('Override Quantity must be a valid number', true);
            return;
          }
          if (price.trim() !== '' && priceNum == null) {
            toast('Forced Unit Price must be a valid number', true);
            return;
          }

          const qtyChanged =
            qtyNum != null &&
            (currentQtyOverride == null || !numsEqual(qtyNum, currentQtyOverride));
          const priceChanged =
            priceNum != null &&
            (currentPriceOverride == null || !numsEqual(priceNum, currentPriceOverride));

          if (!qtyChanged && !priceChanged) {
            toast('No changes to save', true);
            return;
          }

          const saves: Promise<unknown>[] = [];
          if (qtyChanged && qtyNum != null) {
            saves.push(
              saveQty.mutateAsync({ lineId, value: qtyNum, effective_date: effectiveDate })
            );
          }
          if (priceChanged && priceNum != null) {
            const siblingLineIds = siblingMtos
              .filter((m) => selectedMtoIds.has(m.mto_file_id))
              .flatMap((m) => m.line_ids);
            const lineIds = [...new Set([lineId, ...siblingLineIds])];
            if (lineIds.length > 1) {
              saves.push(
                saveBulkPrice.mutateAsync({
                  line_ids: lineIds,
                  unit_price: priceNum,
                  effective_date: effectiveDate,
                })
              );
            } else {
              saves.push(
                savePrice.mutateAsync({ lineId, value: priceNum, effective_date: effectiveDate })
              );
            }
          }

          void Promise.all(saves)
            .then(() => {
              const extra =
                priceChanged && selectedSiblingCount > 0
                  ? ` (price applied to ${selectedSiblingCount + 1} MTO${
                      selectedSiblingCount + 1 === 1 ? '' : 's'
                    })`
                  : '';
              toast(`Saved${extra}`);
              onSaved();
              onClose();
            })
            .catch((err) =>
              toast(err instanceof Error ? err.message : 'Save failed', true)
            );
        }}
      >
        <p className={page.muted} style={{ marginTop: 0 }}>
          Item: {itemLabel}
          {mtoQty != null ? ` · MTO qty ${mtoQty}` : ''}
        </p>
        <p className={page.muted} style={{ marginTop: 0, fontSize: 13 }}>
          Only fields you change are saved with the effective date below. Use Clear on the row to
          remove an override entirely. Forced price can also be applied to other MTOs with the same
          item.
        </p>
        <Input
          label="Override Quantity"
          type="number"
          step="0.01"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder={mtoQty != null ? `MTO qty: ${mtoQty}` : undefined}
          disabled={bidLocked}
        />
        <Input
          label="Forced Unit Price"
          type="number"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          disabled={bidLocked}
        />
        <Input
          label="Effective Date"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          required
          disabled={bidLocked}
        />
        {showApplyToOthers ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Also Apply Price To</div>
            <p className={page.muted} style={{ marginTop: 0, fontSize: 13 }}>
              Other MTOs in this community with the same item and payable task group. This MTO is
              always updated.
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                maxHeight: 220,
                overflow: 'auto',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={bidLocked}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => {
                    setSelectedMtoIds(
                      allSelected ? new Set() : new Set(siblingMtos.map((m) => m.mto_file_id))
                    );
                  }}
                />
                Select All
              </label>
              {siblingMtos.map((m) => (
                <label
                  key={m.mto_file_id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={selectedMtoIds.has(m.mto_file_id)}
                    disabled={bidLocked}
                    onChange={() => {
                      setSelectedMtoIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(m.mto_file_id)) next.delete(m.mto_file_id);
                        else next.add(m.mto_file_id);
                        return next;
                      });
                    }}
                  />
                  <span>
                    {m.mto_name.trim() ? m.mto_name : `MTO ${m.mto_file_id}`}
                    {m.qty != null ? ` · qty ${m.qty}` : ''}
                    {m.price_override != null ? ` · forced $${m.price_override}` : ''}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
        {price.trim() !== '' && siblingsQuery.isLoading ? (
          <p className={page.muted} style={{ fontSize: 13, marginBottom: 0 }}>
            Checking other MTOs for this item…
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="success" disabled={bidLocked || isPending}>
            {isPending
              ? 'Saving…'
              : showApplyToOthers && selectedSiblingCount > 0
                ? `Save To ${selectedSiblingCount + 1} MTOs`
                : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function LineOverrideEditModal({
  open,
  kind,
  lineId,
  itemLabel,
  currentValue,
  mtoId,
  communityId,
  bidLocked,
  onClose,
  onSaved,
}: {
  open: boolean;
  kind: LineOverrideKind;
  lineId: number;
  itemLabel: string;
  currentValue?: number | null;
  mtoId: string | undefined;
  communityId: string | undefined;
  bidLocked?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const save = useSaveLineOverride(mtoId, communityId, kind);
  const [value, setValue] = useState(currentValue != null ? String(currentValue) : '');
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={lineOverrideEditTitle(kind, itemLabel)}
      showClose={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (bidLocked) return;
          const num = Number(value);
          if (!Number.isFinite(num)) {
            toast(`${lineOverrideKindLabel(kind)} is required`, true);
            return;
          }
          save.mutate(
            { lineId, value: num, effective_date: effectiveDate },
            {
              onSuccess: () => {
                toast('Saved');
                onSaved();
                onClose();
              },
              onError: (err) => toast(err instanceof Error ? err.message : 'Save failed', true),
            }
          );
        }}
      >
        <p className={page.muted} style={{ marginTop: 0 }}>
          Item: {itemLabel}
        </p>
        <Input
          label={lineOverrideKindLabel(kind)}
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          disabled={bidLocked}
        />
        <Input
          label="Effective Date"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          required
          disabled={bidLocked}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="success" disabled={bidLocked || save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function LineOverrideHistoryModal({
  open,
  kind,
  lineId,
  itemLabel,
  source = 'mto',
  onClose,
}: {
  open: boolean;
  kind: LineOverrideKind;
  lineId: number | null;
  itemLabel: string;
  source?: 'mto' | 'manual';
  onClose: () => void;
}) {
  const mtoHistory = useLineOverrideHistory(source === 'mto' ? lineId : null, open ? kind : null);
  const manualHistory = useManualMtoLineHistory(
    source === 'manual' && (kind === 'qty' || kind === 'price') ? lineId : null,
    open && source === 'manual' && (kind === 'qty' || kind === 'price') ? kind : null
  );
  const { data, isLoading } = source === 'manual' ? manualHistory : mtoHistory;

  const cols: ColumnDef<HistoryRow>[] = [
    {
      header: 'Effective Date',
      accessorKey: 'effective_date',
      cell: ({ row }) => formatSupplierDate(row.original.effective_date),
    },
    {
      header: lineOverrideKindLabel(kind, source),
      accessorKey: 'value',
      meta: { align: 'right' },
      cell: ({ row }) =>
        Number(row.original.value).toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      header: 'Updated',
      accessorKey: 'updated_at',
      cell: ({ row }) => formatSupplierDate(row.original.updated_at),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={lineOverrideHistoryTitle(kind, itemLabel, source)}
      showClose={false}
    >
      {isLoading ? <p className={page.muted}>Loading…</p> : null}
      {!isLoading && data?.length ? (
        <DataTable
          data={data as HistoryRow[]}
          columns={cols}
          searchKeys={[
            (r) => formatSupplierDate(r.effective_date),
            (r) =>
              Number(r.value).toLocaleString(undefined, { minimumFractionDigits: 2 }),
            (r) => formatSupplierDate(r.updated_at),
          ]}
          searchPlaceholder="Date or value…"
        />
      ) : null}
      {!isLoading && !data?.length ? (
        <p className={page.muted}>No history for this line.</p>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

export function useLineOverrideModalState() {
  const [edit, setEdit] = useState<{
    lineId: number;
    kind: LineOverrideKind;
    itemLabel: string;
    currentValue?: number | null;
  } | null>(null);
  const [qtyPriceEdit, setQtyPriceEdit] = useState<{
    lineId: number;
    itemLabel: string;
    mtoQty?: number | null;
    currentQtyOverride?: number | null;
    currentPriceOverride?: number | null;
  } | null>(null);
  const [history, setHistory] = useState<{
    lineId: number;
    kind: LineOverrideKind;
    itemLabel: string;
    source?: 'mto' | 'manual';
  } | null>(null);
  const [pendingClear, setPendingClear] = useState<{
    lineId: number;
    kind: LineOverrideKind;
  } | null>(null);

  return {
    edit,
    setEdit,
    qtyPriceEdit,
    setQtyPriceEdit,
    history,
    setHistory,
    pendingClear,
    setPendingClear,
  };
}

export function LineOverrideClearConfirm({
  pending,
  mtoId,
  communityId,
  onCancel,
  onCleared,
}: {
  pending: { lineId: number; kind: LineOverrideKind } | null;
  mtoId: string | undefined;
  communityId: string | undefined;
  onCancel: () => void;
  onCleared: () => void;
}) {
  const toast = useToast();
  const clearQty = useClearLineOverride(mtoId, communityId, 'qty');
  const clearPrice = useClearLineOverride(mtoId, communityId, 'price');
  const clearManual = useClearLineOverride(mtoId, communityId, 'manual_cost');

  const clearMut =
    pending?.kind === 'qty' ? clearQty : pending?.kind === 'price' ? clearPrice : clearManual;

  return (
    <ConfirmModal
      open={pending !== null}
      title="Clear Override?"
      confirmLabel="Clear"
      onCancel={onCancel}
      onConfirm={() => {
        if (!pending) return;
        clearMut.mutate(pending.lineId, {
          onSuccess: () => {
            toast('Override cleared');
            onCleared();
            onCancel();
          },
          onError: (e) => toast(e instanceof Error ? e.message : 'Clear failed', true),
        });
      }}
      isPending={clearMut.isPending}
    >
      <p style={{ margin: 0 }}>
        Remove all effective-dated {pending ? lineOverrideKindLabel(pending.kind).toLowerCase() : ''}{' '}
        entries for this line?
      </p>
    </ConfirmModal>
  );
}
